import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  BarChart3,
  CreditCard,
  Eye,
  FileText,
  Search,
  SwatchBook,
  Users,
} from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import AdminLineChartTooltip from "@/components/admin/AdminLineChartTooltip";
import { useAuth } from "@/contexts/auth";
import { useModerationQueue } from "@/hooks/admin/useModerationQueue";
import { useAdminSubscriptions } from "@/hooks/useAdminSubscriptions";
import { adminService } from "@/services/adminService";
import { getBasicAnalytics, getTimeRangeAnalytics } from "@/services/analyticsQueriesService";
import { iconNames } from "@/constants/iconNames";

import { AdminEmptyState, AdminMetricCard, AdminPanel } from "../AdminSurface";

const formatNumber = (value: number) => new Intl.NumberFormat("en-US").format(value);

const AdminDashboardStats = () => {
  const { user } = useAuth();
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ["admin-dashboard-overview"],
    queryFn: getBasicAnalytics,
  });
  const { data: activity, isLoading: activityLoading } = useQuery({
    queryKey: ["admin-dashboard-activity"],
    queryFn: () => getTimeRangeAnalytics(14),
  });
  const { data: moderationQueue = [], isLoading: queueLoading } = useModerationQueue("all", !!user);
  const { data: reviewBundle, isLoading: reportsLoading } = useQuery({
    queryKey: ["admin-dashboard-reported-reviews"],
    queryFn: () => adminService.getReportedReviewsBundle(),
    enabled: !!user,
  });
  const { data: subscriptions = [], isLoading: subscriptionsLoading } = useAdminSubscriptions();

  const pendingListings = moderationQueue.filter((item) => item.status === "pending").length;
  const unassignedListings = moderationQueue.filter((item) => item.flags.isUnassigned).length;
  const unresolvedReports = reviewBundle?.reports.filter((report) => !report.resolved).length ?? 0;
  const pendingVerifications = subscriptions.filter(
    (subscription) => ["grace_period", "requires_action", "past_due", "canceled"].includes(subscription.status || ""),
  ).length;

  const overviewCards = [
    {
      label: "Users",
      value: formatNumber(overview?.totalUsers ?? 0),
      icon: Users,
      tone: "neutral" as const,
    },
    {
      label: "Listings",
      value: formatNumber(overview?.totalListings ?? 0),
      icon: FileText,
      tone: "accent" as const,
    },
    {
      label: "Reviews",
      value: formatNumber(overview?.totalReviews ?? 0),
      icon: AlertCircle,
      tone: "neutral" as const,
    },
    {
      label: "Views",
      value: formatNumber(overview?.totalViews ?? 0),
      icon: Eye,
      tone: "accent" as const,
    },
  ];

  const recentSearchTerms = activity?.topSearchTerms?.slice(0, 5) ?? [];

  const activityChartData = useMemo(
    () =>
      (activity?.viewsByDay ?? []).map((item, index) => ({
        date: item.date,
        views: item.views,
        searches: activity?.searchesByDay?.[index]?.searches ?? 0,
      })),
    [activity?.searchesByDay, activity?.viewsByDay],
  );

  const isLoading =
    overviewLoading || activityLoading || queueLoading || reportsLoading || subscriptionsLoading;

  if (isLoading) {
    return (
      <div className="grid gap-5">
        <div className="grid gap-4 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="h-32 animate-pulse rounded-lg border border-gray-200 bg-white shadow-sm" />
          ))}
        </div>
        <div className="grid gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-28 animate-pulse rounded-lg border border-gray-200 bg-white shadow-sm" />
          ))}
        </div>
        <div className="grid gap-5 xl:grid-cols-[1.35fr_0.65fr]">
          <div className="h-[320px] animate-pulse rounded-lg border border-gray-200 bg-white shadow-sm" />
          <div className="h-[320px] animate-pulse rounded-lg border border-gray-200 bg-white shadow-sm" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPanel title="Needs Attention">
        <div className="grid gap-4 lg:grid-cols-3">
          <AdminMetricCard
            label="Pending listings"
            value={formatNumber(pendingListings)}
            description={unassignedListings > 0 ? `${formatNumber(unassignedListings)} unassigned` : "Review queue is assigned"}
            icon={FileText}
            tone={pendingListings > 0 ? "warning" : "neutral"}
            href="/admin/review-listings"
          />
          <AdminMetricCard
            label="Reported reviews"
            value={formatNumber(unresolvedReports)}
            description="Open reports waiting for action"
            icon={AlertCircle}
            tone={unresolvedReports > 0 ? "warning" : "neutral"}
            href="/admin/reported-reviews"
          />
          <AdminMetricCard
            label="Billing attention"
            value={formatNumber(pendingVerifications)}
            description="Stripe-managed subscriptions that need follow-up"
            icon={CreditCard}
            tone={pendingVerifications > 0 ? "accent" : "neutral"}
            href="/admin/subscriptions"
          />
        </div>
      </AdminPanel>

      <AdminPanel title="Overview">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {overviewCards.map((card) => (
            <AdminMetricCard
              key={card.label}
              label={card.label}
              value={card.value}
              icon={card.icon}
              tone={card.tone}
            />
          ))}
        </div>
      </AdminPanel>

      <AdminPanel title="Tools">
        <div className="grid gap-4 lg:grid-cols-3">
          <AdminMetricCard
            label="Icon browser"
            value={formatNumber(iconNames.length)}
            description="Browse the icon names available for categories and listing UI."
            icon={SwatchBook}
            tone="accent"
            href="/admin/icons"
          />
        </div>
      </AdminPanel>

      <div className="grid gap-5 xl:grid-cols-[1.35fr_0.65fr]">
        <AdminPanel title="Last 14 Days" contentClassName="p-0">
          {activityChartData.length > 0 ? (
            <div className="px-3 pb-3 pt-4 sm:px-5 sm:pb-5">
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={activityChartData} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                    <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fill: "#6b7280", fontSize: 12 }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fill: "#6b7280", fontSize: 12 }} />
                    <Tooltip content={<AdminLineChartTooltip />} />
                    <Line type="monotone" dataKey="views" stroke="#25D366" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="searches" stroke="#4b5563" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="p-5">
              <AdminEmptyState
                icon={BarChart3}
                title="No recent activity"
                description="Views and searches will appear here when enough recent data is available."
              />
            </div>
          )}
        </AdminPanel>

        <AdminPanel title="Top Searches">
          {recentSearchTerms.length > 0 ? (
            <div className="space-y-3">
              {recentSearchTerms.map((term, index) => (
                <div
                  key={term.term}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">#{index + 1}</p>
                    <p className="truncate text-sm font-medium text-gray-800">{term.term}</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-700">{formatNumber(term.count)}</p>
                </div>
              ))}

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-500">Contacts</p>
                  <p className="mt-1 text-lg font-semibold text-gray-800">
                    {formatNumber(activity?.totalContacts ?? 0)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-500">Impressions</p>
                  <p className="mt-1 text-lg font-semibold text-gray-800">
                    {formatNumber(activity?.totalImpressions ?? 0)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <AdminEmptyState
              icon={Search}
              title="No search data"
              description="Top search terms will show here when enough search activity has been recorded."
            />
          )}
        </AdminPanel>
      </div>
    </div>
  );
};

export default AdminDashboardStats;
